import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar,
  CheckCircle2,
  ExternalLink,
  FolderKanban,
  Loader2,
  Tag,
  ThumbsDown,
  ThumbsUp,
  User,
  X,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  todayInWorkspaceTz,
  taskPriorityValue,
  type UpdateTaskInput,
  type WorkspaceTask,
} from "@/hooks/useWorkspaceTasks";
import { useWorkspaceProjects, useProjectColumns } from "@/hooks/useWorkspaceProjects";
import { TASK_PRIORITY_OPTIONS, type TaskPriority } from "@/lib/task-priority";
import { getTaskUrgency } from "@/lib/task-urgency";
import {
  buildTaskBreadcrumbs,
  fetchWorkspaceTaskDetail,
  type TaskBreadcrumbItem,
} from "@/lib/task-detail-navigation";
import { TaskTagsInput } from "./TaskTagsInput";
import { TaskLeadSelect } from "./TaskLeadSelect";
import { TaskDatePicker } from "./TaskDatePicker";
import { TaskTimePicker } from "./TaskTimePicker";
import { TaskSubtaskSection } from "./TaskSubtaskSection";
import { TaskAttachedMessagesSection } from "./TaskAttachedMessagesSection";
import { TaskPriorityBadge } from "./TaskPriorityBadge";
import { TaskDetailBreadcrumb } from "./TaskDetailBreadcrumb";
import { cn } from "@/lib/utils";

interface WorkspaceMemberOption {
  id: string;
  display_name: string | null;
  user_id: string;
}

interface TaskDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: WorkspaceTask | null;
  workspaceId: string;
  memberId: string;
  isAdmin: boolean;
  onSave: (input: UpdateTaskInput) => Promise<void>;
  onComplete?: (task: WorkspaceTask) => void;
}

function isOverdueTask(task: WorkspaceTask): boolean {
  if (!task.due_date || task.status !== "pending") return false;
  return task.due_date < todayInWorkspaceTz();
}

function statusLabel(status: WorkspaceTask["status"]): string {
  switch (status) {
    case "completed":
      return "Concluída";
    case "cancelled":
      return "Cancelada";
    default:
      return "Pendente";
  }
}

function MetaRow({
  icon: Icon,
  label,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-1.5 text-sm">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="text-xs">{label}</span>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function TaskDetailSheet({
  open,
  onOpenChange,
  task,
  workspaceId,
  memberId,
  isAdmin,
  onSave,
  onComplete,
}: TaskDetailSheetProps) {
  const [activeTask, setActiveTask] = useState<WorkspaceTask | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<TaskBreadcrumbItem[]>([]);
  const [navigating, setNavigating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState(memberId);
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [tags, setTags] = useState<string[]>([]);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [noDueDate, setNoDueDate] = useState(false);
  const [dueDate, setDueDate] = useState(todayInWorkspaceTz());
  const [dueTime, setDueTime] = useState("");
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [columnId, setColumnId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const skipAutoColumnRef = useRef(false);
  const { toast } = useToast();
  const { projects } = useWorkspaceProjects(workspaceId);
  const { columns, loading: columnsLoading } = useProjectColumns(projectId);

  const displayTask = activeTask ?? task;

  const projectName = useMemo(() => {
    const pid = displayTask?.project_id;
    if (!pid) return "Inbox";
    return projects.find((p) => p.id === pid)?.name ?? "Projeto";
  }, [displayTask?.project_id, projects]);

  const allowPastDue = useMemo(
    () => (displayTask ? isOverdueTask(displayTask) : false),
    [displayTask],
  );
  const isRootTask = Boolean(displayTask && displayTask.parent_task_id == null);
  const canManageSubtasks = Boolean(
    displayTask &&
      displayTask.status !== "cancelled" &&
      (isAdmin || displayTask.assigned_member_id === memberId),
  );
  const isReadOnly = !displayTask || displayTask.status === "cancelled";
  const urgency = displayTask
    ? getTaskUrgency({
        status: displayTask.status,
        due_date: displayTask.due_date,
        due_at: displayTask.due_at,
      })
    : null;

  const refreshBreadcrumbs = useCallback(
    async (taskId: string, name: string) => {
      try {
        const items = await buildTaskBreadcrumbs(taskId, name);
        setBreadcrumbs(items);
      } catch {
        setBreadcrumbs([]);
      }
    },
    [],
  );

  const navigateToTask = useCallback(
    async (taskId: string) => {
      if (!taskId || taskId === displayTask?.id) return;
      try {
        setNavigating(true);
        const loaded = await fetchWorkspaceTaskDetail(taskId);
        if (!loaded) {
          toast({ title: "Tarefa não encontrada", variant: "destructive" });
          return;
        }
        setActiveTask(loaded);
        const pname =
          projects.find((p) => p.id === loaded.project_id)?.name ?? "Inbox";
        await refreshBreadcrumbs(loaded.id, pname);
      } catch (err: unknown) {
        toast({
          title: "Erro ao abrir tarefa",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      } finally {
        setNavigating(false);
      }
    },
    [displayTask?.id, projects, refreshBreadcrumbs, toast],
  );

  useEffect(() => {
    if (!open) {
      setActiveTask(null);
      setBreadcrumbs([]);
      return;
    }
    if (task) setActiveTask(task);
  }, [open, task?.id, task]);

  useEffect(() => {
    if (!open || !displayTask) return;
    const pname =
      projects.find((p) => p.id === displayTask.project_id)?.name ?? "Inbox";
    void refreshBreadcrumbs(displayTask.id, pname);
  }, [open, displayTask?.id, displayTask?.project_id, projects, refreshBreadcrumbs]);

  useEffect(() => {
    if (!open || !displayTask) return;
    setTitle(displayTask.title);
    setDescription(displayTask.description ?? "");
    setAssigneeId(displayTask.assigned_member_id);
    setPriority(taskPriorityValue(displayTask));
    setTags(displayTask.tags ?? []);
    setLeadId(displayTask.lead_id ?? null);
    setProjectId(displayTask.project_id ?? null);
    setColumnId(displayTask.project_column_id ?? null);
    skipAutoColumnRef.current = Boolean(
      displayTask.project_id && !displayTask.project_column_id,
    );
    const hasDate = Boolean(displayTask.due_date);
    setNoDueDate(!hasDate);
    setDueDate(displayTask.due_date ?? todayInWorkspaceTz());
    if (displayTask.due_at) {
      const d = new Date(displayTask.due_at);
      setDueTime(
        `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      );
    } else {
      setDueTime("");
    }
  }, [open, displayTask]);

  useEffect(() => {
    if (!open || !isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name, user_id")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as WorkspaceMemberOption[]);
    })();
  }, [open, workspaceId, isAdmin]);

  useEffect(() => {
    if (!open || !projectId || columnsLoading) return;
    if (columns.length === 0) {
      setColumnId(null);
      return;
    }
    if (skipAutoColumnRef.current) return;
    setColumnId((current) => {
      if (current && columns.some((c) => c.id === current)) return current;
      return columns[0]?.id ?? null;
    });
  }, [open, projectId, columns, columnsLoading]);

  const handleSubmit = async () => {
    if (!displayTask) return;
    if (!title.trim()) {
      toast({ title: "Informe um título", variant: "destructive" });
      return;
    }
    if (!noDueDate && !allowPastDue) {
      const today = todayInWorkspaceTz();
      if (dueDate < today) {
        toast({
          title: "Data inválida",
          description: "Não é possível definir data no passado.",
          variant: "destructive",
        });
        return;
      }
    }
    try {
      setSubmitting(true);
      const due_date = noDueDate ? null : dueDate;
      const due_at =
        !noDueDate && dueDate && dueTime
          ? new Date(`${dueDate}T${dueTime}:00`).toISOString()
          : null;
      await onSave({
        taskId: displayTask.id,
        title: title.trim(),
        description: description.trim() || null,
        assigned_member_id: isAdmin ? assigneeId : displayTask.assigned_member_id,
        due_date,
        due_at,
        priority,
        lead_id: leadId,
        tags,
        allowPastDue,
        project_id: isRootTask ? projectId : displayTask.project_id ?? null,
        project_column_id:
          isRootTask && projectId && columnId ? columnId : null,
      });
      const refreshed = await fetchWorkspaceTaskDetail(displayTask.id);
      if (refreshed) {
        setActiveTask(refreshed);
        const pname =
          projects.find((p) => p.id === refreshed.project_id)?.name ?? "Inbox";
        await refreshBreadcrumbs(refreshed.id, pname);
      }
      toast({ title: "Tarefa atualizada" });
    } catch (err: unknown) {
      toast({
        title: "Erro ao atualizar tarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  if (!displayTask) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideClose
        className="flex h-full w-full flex-col gap-0 border-l p-0 sm:max-w-xl md:max-w-2xl lg:max-w-[680px]"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Detalhes da tarefa</SheetTitle>
        </SheetHeader>

        <div className="flex shrink-0 items-center justify-between border-b px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            {displayTask.status === "pending" && onComplete && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 shrink-0"
                onClick={() => onComplete(displayTask)}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Marcar como concluída
              </Button>
            )}
            {navigating && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar painel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <TaskDetailBreadcrumb
            items={breadcrumbs}
            activeTaskId={displayTask.id}
            onNavigate={navigateToTask}
          />

          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            readOnly={isReadOnly}
            placeholder="Nome da tarefa"
            className="border-0 px-0 text-xl font-semibold shadow-none focus-visible:ring-0 h-auto py-0"
          />

          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-xs">
              {statusLabel(displayTask.status)}
            </Badge>
            <TaskPriorityBadge priority={priority} />
            {urgency?.label && (
              <Badge
                variant={urgency.badgeVariant ?? "outline"}
                className={cn("text-xs", urgency.badgeClassName)}
              >
                {urgency.label}
              </Badge>
            )}
            {displayTask.status === "completed" && displayTask.outcome === "positive" && (
              <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 text-xs">
                <ThumbsUp className="h-3 w-3 mr-1" /> Positivo
              </Badge>
            )}
            {displayTask.status === "completed" && displayTask.outcome === "negative" && (
              <Badge className="bg-rose-500/15 text-rose-600 hover:bg-rose-500/15 text-xs">
                <ThumbsDown className="h-3 w-3 mr-1" /> Negativo
              </Badge>
            )}
          </div>

          <div className="rounded-lg border divide-y bg-muted/10 px-3">
            <MetaRow icon={User} label="Responsável">
              {isAdmin && !isReadOnly ? (
                <Select value={assigneeId} onValueChange={setAssigneeId}>
                  <SelectTrigger className="h-8 bg-background text-sm">
                    <SelectValue placeholder="Selecionar membro" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.display_name || "(sem nome)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm">
                  {displayTask.assignee?.display_name || "Sem responsável"}
                </span>
              )}
            </MetaRow>

            <MetaRow icon={Calendar} label="Data">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="task-detail-no-due-date"
                    checked={noDueDate}
                    disabled={isReadOnly}
                    onCheckedChange={(v) => setNoDueDate(v === true)}
                  />
                  <Label
                    htmlFor="task-detail-no-due-date"
                    className="cursor-pointer text-xs font-normal"
                  >
                    Sem data
                  </Label>
                </div>
                {!noDueDate && (
                  <div className="grid grid-cols-2 gap-2">
                    <TaskDatePicker
                      id="task-detail-date"
                      value={dueDate}
                      onChange={setDueDate}
                      minDate={allowPastDue ? undefined : todayInWorkspaceTz()}
                      disabled={isReadOnly}
                    />
                    <TaskTimePicker
                      id="task-detail-time"
                      value={dueTime}
                      onChange={setDueTime}
                      clearable
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>
            </MetaRow>

            <MetaRow icon={FolderKanban} label="Projeto">
              {isRootTask && !isReadOnly ? (
                <Select
                  value={projectId ?? "none"}
                  onValueChange={(v) => {
                    skipAutoColumnRef.current = false;
                    setProjectId(v === "none" ? null : v);
                    setColumnId(null);
                  }}
                >
                  <SelectTrigger className="h-8 bg-background text-sm">
                    <SelectValue placeholder="Sem projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Inbox</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm">{projectName}</span>
              )}
            </MetaRow>

            {projectId && columns.length > 0 && isRootTask && (
              <MetaRow icon={FolderKanban} label="Coluna">
                <Select
                  value={columnId ?? "none"}
                  disabled={isReadOnly}
                  onValueChange={(v) => {
                    skipAutoColumnRef.current = v === "none";
                    setColumnId(v === "none" ? null : v);
                  }}
                >
                  <SelectTrigger className="h-8 bg-background text-sm">
                    <SelectValue placeholder="Sem coluna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem coluna</SelectItem>
                    {columns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </MetaRow>
            )}

            <MetaRow icon={Tag} label="Prioridade">
              <Select
                value={priority}
                onValueChange={(v) => setPriority(v as TaskPriority)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="h-8 bg-background text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </MetaRow>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <TaskLeadSelect
              workspaceId={workspaceId}
              value={leadId}
              onChange={setLeadId}
              linkedLeadName={displayTask.lead?.name}
            />
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <TaskTagsInput tags={tags} onChange={setTags} />
            </div>
          </div>

          {displayTask.lead?.name && displayTask.lead_id && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Lead: {displayTask.lead.name}</span>
              <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
                <a
                  href={`/conversations?lead_id=${displayTask.lead_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-3 w-3 mr-1 inline" />
                  Ver conversa
                </a>
              </Button>
            </div>
          )}

          {displayTask.creator?.display_name && (
            <p className="text-xs text-muted-foreground">
              Criada por {displayTask.creator.display_name}
              {displayTask.completed_at && (
                <>
                  {" · Concluída em "}
                  {format(parseISO(displayTask.completed_at), "dd/MM/yyyy HH:mm", {
                    locale: ptBR,
                  })}
                </>
              )}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="task-detail-description" className="text-sm font-medium">
              Descrição
            </Label>
            <Textarea
              id="task-detail-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              readOnly={isReadOnly}
              rows={10}
              className="min-h-[200px] resize-y text-sm leading-relaxed"
              placeholder="Adicione detalhes, contexto ou um checklist."
            />
          </div>

          <TaskAttachedMessagesSection
            key={displayTask.id}
            taskId={displayTask.id}
            workspaceId={workspaceId}
            leadId={leadId}
            active={open}
            canDetach={!isReadOnly}
          />

          <div className="border-t pt-3">
            <TaskSubtaskSection
              key={displayTask.id}
              parentTaskId={displayTask.id}
              workspaceId={workspaceId}
              memberId={memberId}
              isAdmin={isAdmin}
              canManage={canManageSubtasks}
              minDate={allowPastDue ? undefined : todayInWorkspaceTz()}
              parentDueDate={displayTask.due_date}
              onOpenSubtask={navigateToTask}
            />
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
              Fechar
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
